/**
 * V112-3-10 (ADR-0099 §5, owner D1–D5): a system's Scope tab — the one editable scope screen, system context.
 *
 * "From workspace defaults": the inheritable answers, each "Inherited from workspace" (the workspace value, read only)
 * or "Set for this system" (who / when, the editor, Reset to workspace). The sector is one per system (D3); a sector
 * kept when the workspace named several industries reads "Waiting on sector" (D5).
 * "This system only": who has the final say (D2), your role (ActorRoleQuestion), general-purpose AI role, exposure,
 * output destinations, embedded third-party AI, deployment target, security levels. Data categories and affected
 * parties are not asked (D1).
 *
 * "Leads to" under each answer lists the rules that apply now and read it (the dry run's `current`, `depends_on`).
 * Edits collect in a draft; Review changes runs the engine dry run (POST …/scope-preview) and shows +/− per rule in
 * the sheet; Confirm saves through PATCH …/classification (owner / admin; the server enforces it).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Field, Input, Radio, Select, Spinner } from '@aigovops/design-system';
import { effectiveScope, overridesOf, type FinalSay, type ScopeAnswer, type ScopeOverrideMeta, type SystemScopePreview } from '@aigovops/core';
import { getWorkspaceProfile, previewSystemScope, updateSystemClassification, type WsSystemRow } from '../../lib/api';
import { queryKeys, useInvalidate } from '../../lib/query-keys';
import { useWorkspaceQuery } from '../../hooks/useWorkspaceQuery';
import { ActorRoleQuestion, confirmedActorRoles } from '../ActorRoleQuestion';
import { Caret, ScopeAnswerRow, ScopeChip } from './ScopeAnswerRow';
import { RowEditor, SectorPicks } from './ScopeEditors';
import { ScopeSaveBar } from './ScopeSaveBar';
import { ScopeSummaryStrip, focusAnswer, type StripCell } from './ScopeSummaryStrip';
import {
  CIA_DIMS, EXPOSURE_CHOICES, FINAL_SAY_CHOICES, GPAI_CHOICES, LEVEL_CHOICES, OUTPUT_CHOICES, REMOVE_NOTE, ROW_ANSWERS, ROW_HINT, ROW_KEYS,
  SYSTEM_ROW_NAME, TARGET_CHOICES, canonicalOutputs, changeWords, choiceLabel, classificationRows, depRows, depText, finalSayFields, finalSayOf, fmt, inheritedSector,
  parseJson, plural, profileDefaults, profileRows, rowBody, rowMeta, sameSet, systemDecides, valueItems,
  type Classification, type RowKey,
} from './scope-options';
import './scope.css';

/** System-only answers, keyed as core SYSTEM_ANSWER_FIELDS (their D4 stamp keys and the preview's depends_on keys). */
type OnlyKey = 'final_say' | 'gpai_role' | 'exposure' | 'output_destinations' | 'embedded_third_party_ai' | 'deployment_target' | 'security_levels';
interface OnlyValues {
  final_say: string | null; gpai_role: string | null; exposure: string | null; output_destinations: string[];
  embedded_third_party_ai: string[] | null; deployment_target: string | null; security_levels: Record<string, string>;
}
const ONLY_KEYS: OnlyKey[] = ['final_say', 'gpai_role', 'exposure', 'output_destinations', 'embedded_third_party_ai', 'deployment_target', 'security_levels'];
const ONLY_NAME: Record<OnlyKey, string> = {
  final_say: 'Who has the final say?', gpai_role: 'General-purpose AI role', exposure: 'Exposure', output_destinations: 'Output destinations',
  embedded_third_party_ai: 'Embedded third-party AI', deployment_target: 'Deployment target', security_levels: 'Security levels',
};
interface RowDraft { own: boolean; value: string[] }
type Key = RowKey | OnlyKey | 'fit' | 'your_role';

const strings = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : []);
const str = (x: unknown): string | null => (typeof x === 'string' && x ? x : null);
const sameOnly = (a: unknown, b: unknown) => JSON.stringify(Array.isArray(a) ? [...a].sort() : a) === JSON.stringify(Array.isArray(b) ? [...b].sort() : b);
const answered = (k: OnlyKey, v: OnlyValues) =>
  k === 'security_levels' ? CIA_DIMS.every(([d]) => !!v.security_levels[d])
    : k === 'embedded_third_party_ai' ? v.embedded_third_party_ai !== null
      : k === 'output_destinations' ? v.output_destinations.length > 0 : !!v[k];
/** A system-only answer's value in words (null = not answered). */
function onlyWords(k: string, v: OnlyValues): string | null {
  switch (k) {
    case 'final_say': return v.final_say ? choiceLabel(FINAL_SAY_CHOICES, v.final_say) : null;
    case 'gpai_role': return v.gpai_role ? choiceLabel(GPAI_CHOICES, v.gpai_role) : null;
    case 'exposure': return v.exposure ? choiceLabel(EXPOSURE_CHOICES, v.exposure) : null;
    case 'deployment_target': return v.deployment_target ? choiceLabel(TARGET_CHOICES, v.deployment_target) : null;
    case 'output_destinations': return v.output_destinations.length ? v.output_destinations.map((x) => choiceLabel(OUTPUT_CHOICES, x)).join(', ') : null;
    case 'embedded_third_party_ai': return v.embedded_third_party_ai === null ? null : v.embedded_third_party_ai.length ? v.embedded_third_party_ai.join(', ') : 'None';
    case 'security_levels': return CIA_DIMS.some(([d]) => v.security_levels[d])
      ? CIA_DIMS.map(([d, l]) => `${l} ${v.security_levels[d] ? choiceLabel(LEVEL_CHOICES, v.security_levels[d]!) : 'not answered'}`).join(', ') : null;
    default: return null;
  }
}
type Rule = SystemScopePreview['added'][number];

export function SystemScope({ slug, system, canEdit, onSaved }: {
  slug: string; system: WsSystemRow; canEdit: boolean; onSaved: () => void;
}) {
  const prof = useWorkspaceQuery((s) => queryKeys.scopeProfile(s), (s) => getWorkspaceProfile(s));
  if (prof.loading) return <Spinner label="Loading scope" />;
  if (prof.error) return <Alert variant="error">The workspace defaults did not load. <Button compact onClick={() => void prof.refetch()}>Retry</Button></Alert>;
  const profile = prof.data?.profile;
  if (!profile) {
    return <Alert variant="warning">This workspace has no defaults yet. <Link to={`/w/${slug}/app/settings/profile`}>Set workspace defaults</Link></Alert>;
  }
  return <SystemScopeForm slug={slug} system={system} canEdit={canEdit} onSaved={onSaved} profile={profile} />;
}

function SystemScopeForm({ slug, system, canEdit, onSaved, profile }: {
  slug: string; system: WsSystemRow; canEdit: boolean; onSaved: () => void;
  profile: NonNullable<Awaited<ReturnType<typeof getWorkspaceProfile>>['profile']>;
}) {
  const invalidate = useInvalidate();
  // ---- Saved state ----
  const saved = useMemo(() => {
    const C = parseJson<Classification>(system.classification_json, {});
    const classified = !!system.classification_json;
    const rawOverrides = parseJson<unknown>(system.scope_overrides_json ?? null, null);
    const stored = Array.isArray(rawOverrides) ? strings(rawOverrides) : null;
    const meta = parseJson<ScopeOverrideMeta | null>(system.scope_override_meta_json ?? null, null);
    // A system never classified inherits every answer (nothing of its own yet).
    const overrides = stored ?? (classified ? null : []);
    const own = overridesOf(overrides, C);
    const eff = effectiveScope(C, profileDefaults(profile), overrides);
    const effRows = classificationRows(eff);
    const rows = Object.fromEntries(ROW_KEYS.map((r) => [r, { own: ROW_ANSWERS[r].some((a) => own.includes(a)), value: effRows[r] }])) as Record<RowKey, RowDraft>;
    const cia = parseJson<Record<string, unknown>>(system.data_classification_json, {});
    const only: OnlyValues = {
      final_say: finalSayOf(C),
      gpai_role: str(C.gpai_role),
      exposure: str(C.exposure_level) ?? str(system.exposure_level),
      // An older alias (end_user) reads as the option it means and is saved as it on the next save.
      output_destinations: canonicalOutputs(C.output_destinations !== undefined ? strings(C.output_destinations) : strings(parseJson<unknown>(system.output_destinations_json, []))),
      embedded_third_party_ai: Array.isArray(C.embedded_third_party_ai) ? strings(C.embedded_third_party_ai) : null,
      deployment_target: str(C.deployment_target),
      security_levels: Object.fromEntries(CIA_DIMS.map(([d]) => [d, typeof cia[d] === 'string' ? (cia[d] as string) : ''])),
    };
    return { classified, meta: meta && typeof meta === 'object' ? meta : null, rows, only };
  }, [system, profile]);
  const ws = useMemo(() => profileRows(profile), [profile]);
  const wsIndustries = ws.sector;
  const locked = wsIndustries.length !== 1;
  const wsValue = (r: RowKey) => (r === 'sector' ? inheritedSector(wsIndustries) : ws[r]);
  const isNew = !saved.classified;
  // V112-3-10: the rules that apply now, each with the answers it reads ("Leads to"; a dry run any member may call).
  const leads = useWorkspaceQuery((sl) => queryKeys.scopeLeads(sl, system.system_id), (sl) => previewSystemScope(sl, system.system_id, {}), { retry: false });
  const current: Rule[] = leads.data?.current ?? [];

  // ---- Draft (edits over the saved state) ----
  const [rowEdits, setRowEdits] = useState<Partial<Record<RowKey, RowDraft>>>({});
  const [onlyEdits, setOnlyEdits] = useState<Partial<OnlyValues>>({});
  const [asIs, setAsIs] = useState<Set<Key>>(new Set());
  const [fit, setFit] = useState(false);
  const [fitSaved, setFitSaved] = useState(false);
  const [sheet, setSheet] = useState<{ open: boolean; loading: boolean; preview: SystemScopePreview | null; error: string | null; failed?: 'preview' | 'save' }>({ open: false, loading: false, preview: null, error: null });
  // "Your role" saves on its own route: an unsaved tick holds the leave guard; an unconfirmed role is open.
  const [roleDirty, setRoleDirty] = useState(false);
  const roleOpen = !confirmedActorRoles(system.classification_json);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<ReactNode>(null);
  const [announcement, setAnnouncement] = useState<{ text: string; n: number } | null>(null);
  const [tok, setTok] = useState('');
  const [tokErr, setTokErr] = useState('');
  const say = (text: string) => setAnnouncement((a) => ({ text, n: (a?.n ?? 0) + 1 }));

  const row = (r: RowKey): RowDraft => rowEdits[r] ?? saved.rows[r];
  const rowDirty = (r: RowKey) => {
    const e = rowEdits[r]; const s = saved.rows[r];
    // Opening an editor ("Change for this system") isn't a change until the value differs; handing it back is.
    return !!e && ((!e.own && s.own) || (e.own && !sameSet(e.value, s.value)));
  };
  const only: OnlyValues = { ...saved.only, ...onlyEdits };
  const onlyDirty = (k: OnlyKey) => k in onlyEdits && !sameOnly(onlyEdits[k], saved.only[k]);
  const dirtyKeys: Key[] = [
    ...(fit ? ['fit' as const] : []),
    ...ROW_KEYS.filter((r) => rowDirty(r) || asIs.has(r)),
    ...ONLY_KEYS.filter((k) => onlyDirty(k) || asIs.has(k)),
  ];
  const nameOf = (k: Key) => (k === 'fit' ? 'Workspace answers' : k === 'your_role' ? 'Your role' : (ROW_KEYS as readonly string[]).includes(k) ? SYSTEM_ROW_NAME[k as RowKey] : ONLY_NAME[k as OnlyKey]);
  const rowState = (r: RowKey, d = row(r)) => (d.own ? 'set' : r === 'sector' && locked ? 'open' : 'inherit');
  const openKeys = (useDraft: boolean): Key[] => {
    const o = useDraft ? only : saved.only;
    const sectorOpen = locked && !(useDraft ? row('sector') : saved.rows.sector).own;
    return [
      ...(isNew && !fitSaved && !(useDraft && fit) ? ['fit' as const] : []),
      ...(sectorOpen ? ['sector' as const] : []),
      ...(ONLY_KEYS.slice(0, 1).filter((k) => !answered(k, o))),
      ...(roleOpen ? ['your_role' as const] : []),
      ...ONLY_KEYS.slice(1).filter((k) => !answered(k, o)),
    ];
  };

  const clearDone = () => setDone(null);
  const editRow = (r: RowKey, d: RowDraft) => { clearDone(); setRowEdits((m) => ({ ...m, [r]: d })); setAsIs((s) => { const n = new Set(s); n.delete(r); return n; }); };
  const editOnly = <K extends OnlyKey>(k: K, v: OnlyValues[K]) => { clearDone(); setOnlyEdits((m) => ({ ...m, [k]: v })); setAsIs((s) => { const n = new Set(s); n.delete(k); return n; }); };
  const confirmAsIs = (k: Key) => {
    clearDone(); setAsIs((s) => new Set(s).add(k));
    say(`${nameOf(k)} will be confirmed as is on save; the value doesn’t change.`);
    window.setTimeout(() => focusAnswer(k), 0);
  };
  const undoAsIs = (k: Key) => {
    setAsIs((s) => { const n = new Set(s); n.delete(k); return n; });
    say(`${nameOf(k)} won’t be confirmed.`);
    window.setTimeout(() => focusAnswer(k), 0);
  };

  // ---- The PATCH body (also the preview's) ----
  function buildBody(): Parameters<typeof updateSystemClassification>[2] {
    const body: Record<string, unknown> = {};
    const resets = new Set<ScopeAnswer>();
    for (const r of ROW_KEYS) {
      const d = row(r);
      if (rowDirty(r)) {
        if (d.own) Object.assign(body, rowBody(r, d.value) ?? {});
        else ROW_ANSWERS[r].forEach((a) => resets.add(a));
      } else if (asIs.has(r) && d.own) Object.assign(body, rowBody(r, d.value) ?? {});
    }
    // "These fit": every inherited answer is recorded as inheriting (the save also marks the system classified).
    if (fit) ROW_KEYS.filter((r) => !row(r).own).forEach((r) => ROW_ANSWERS[r].forEach((a) => resets.add(a)));
    for (const k of ONLY_KEYS) {
      if (!onlyDirty(k) && !asIs.has(k)) continue;
      if (k === 'final_say' && only.final_say) Object.assign(body, finalSayFields(only.final_say as FinalSay));
      if (k === 'gpai_role' && only.gpai_role) body.gpai_role = only.gpai_role;
      if (k === 'exposure' && only.exposure) body.exposure_level = only.exposure;
      if (k === 'output_destinations') body.output_destinations = only.output_destinations;
      if (k === 'embedded_third_party_ai' && only.embedded_third_party_ai !== null) body.embedded_third_party_ai = only.embedded_third_party_ai;
      if (k === 'deployment_target' && only.deployment_target) body.deployment_target = only.deployment_target;
      if (k === 'security_levels') {
        const levels = Object.fromEntries(CIA_DIMS.map(([d]) => [d, only.security_levels[d]]).filter(([, v]) => !!v));
        if (Object.keys(levels).length) body.data_classification_json = levels;
      }
    }
    if (resets.size) body.reset_to_workspace = [...resets];
    if (reason.trim()) body.scope_reason = reason.trim();
    return body as Parameters<typeof updateSystemClassification>[2];
  }

  async function review() {
    if (!dirtyKeys.length) return;
    setSheet({ open: true, loading: true, preview: null, error: null });
    try {
      const preview = await previewSystemScope(slug, system.system_id, buildBody());
      setSheet({ open: true, loading: false, preview, error: null });
    } catch {
      setSheet({ open: true, loading: false, preview: null, error: 'We couldn’t check what this changes, so it can’t be saved yet.', failed: 'preview' });
    }
  }
  const closeSheet = (focusKey?: Key) => {
    setSheet((s) => ({ ...s, open: false }));
    if (focusKey) window.setTimeout(() => focusAnswer(focusKey === 'fit' ? 'fit' : focusKey), 0);
  };
  async function confirm() {
    // Never save without a loaded preview (a failed one blocks Confirm).
    if (busy || !sheet.preview) return;
    setBusy(true);
    const resetNames = ROW_KEYS.filter((r) => rowDirty(r) && !row(r).own).map((r) => SYSTEM_ROW_NAME[r]);
    try {
      await updateSystemClassification(slug, system.system_id, buildBody());
      const p = sheet.preview;
      const A = p?.added.length ?? 0, R = p?.removed.length ?? 0;
      setDone(
        <div className="sc-done" tabIndex={-1} id="sc-done">
          <ScopeChip kind="check">Saved</ScopeChip>
          <span>{A || R ? `${[A ? plural(A, 'obligation', 'obligations') + ' added' : '', R ? `${R} removed` : ''].filter(Boolean).join(', ')}.` : 'No obligations changed.'}
            {resetNames.length ? ` ${resetNames.map((n) => `${n} reset to workspace.`).join(' ')}` : ''}</span>
          <Link to={`/w/${slug}/app/systems/${encodeURIComponent(system.system_id)}?tab=requirements`}>View obligations</Link>
        </div>,
      );
      if (fit) setFitSaved(true);
      setRowEdits({}); setOnlyEdits({}); setAsIs(new Set()); setFit(false); setReason('');
      setSheet({ open: false, loading: false, preview: null, error: null });
      say('Saved.');
      invalidate('classification:changed', slug);
      onSaved();
      window.setTimeout(() => document.getElementById('sc-done')?.focus(), 0);
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      setSheet((s) => ({ ...s, failed: 'save', error: status === 401 ? 'Your session has expired. Sign in again, then try again.'
        : status === 403 ? 'Your role in this workspace doesn’t allow saving these answers. An owner or admin can save them.'
          : 'That didn’t save. Nothing changed.' }));
    } finally {
      setBusy(false);
    }
  }
  const discard = () => {
    const first = dirtyKeys[0];
    setRowEdits({}); setOnlyEdits({}); setAsIs(new Set()); setFit(false);
    say('Changes discarded.');
    if (first) window.setTimeout(() => focusAnswer(first), 0);
  };

  // ---- Words for the sheet ----
  function changeText(k: Key): string {
    if (k === 'fit') return 'checked: they fit this system';
    if ((ROW_KEYS as readonly string[]).includes(k)) {
      const r = k as RowKey, s = saved.rows[r], d = row(r);
      if (!rowDirty(r)) return 'confirmed as is';
      if (s.own && !d.own) return 'reset to workspace';
      if (r === 'sector' && locked) return `${s.value[0] ? `${fmt('sector', s.value)} → ` : 'picked '}${fmt('sector', d.value)}`;
      const w = changeWords(r, s.value, d.value);
      if (!s.own && d.own) return w === 'no change' ? 'set for this system; same value, stops following workspace changes' : `set for this system: ${w}`;
      return w;
    }
    const o = k as OnlyKey;
    if (!onlyDirty(o)) return 'confirmed as is';
    const b = saved.only, n = only;
    if (o === 'output_destinations') {
      const plus = n.output_destinations.filter((v) => !b.output_destinations.includes(v)).map((v) => `+ ${choiceLabel(OUTPUT_CHOICES, v)}`);
      const minus = b.output_destinations.filter((v) => !n.output_destinations.includes(v)).map((v) => `− ${choiceLabel(OUTPUT_CHOICES, v)}`);
      return [...plus, ...minus].join(', ');
    }
    if (o === 'embedded_third_party_ai') {
      const now = n.embedded_third_party_ai, was = b.embedded_third_party_ai ?? [];
      if (!now?.length) return 'None';
      return [...now.filter((v) => !was.includes(v)).map((v) => `+ ${v}`), ...was.filter((v) => !now.includes(v)).map((v) => `− ${v}`)].join(', ');
    }
    if (o === 'security_levels') return CIA_DIMS.filter(([d]) => b.security_levels[d] !== n.security_levels[d])
      .map(([d, l]) => `${l} ${b.security_levels[d] ? choiceLabel(LEVEL_CHOICES, b.security_levels[d]!) : 'not answered'} → ${n.security_levels[d] ? choiceLabel(LEVEL_CHOICES, n.security_levels[d]!) : 'not answered'}`).join(', ');
    return `${onlyWords(o, b) ?? 'Not answered'} → ${onlyWords(o, n) ?? 'Not answered'}`;
  }

  // ---- Strip ----
  // The strip shows what is saved; a row with unsaved edits marks its cell "Changed".
  const inhRows = ROW_KEYS.filter((r) => rowState(r, saved.rows[r]) === 'inherit');
  const setRows = ROW_KEYS.filter((r) => rowState(r, saved.rows[r]) === 'set');
  const cellChanged = (rows: RowKey[]) => (rows.some((r) => rowDirty(r) || asIs.has(r)) ? <ScopeChip kind="changed">Changed</ScopeChip> : null);
  const openDraft = openKeys(true), openSaved = openKeys(false);
  const total = ONLY_KEYS.length + 1 + (locked ? 1 : 0) + (isNew && !fitSaved ? 1 : 0);
  const openName = (k: Key) => (k === 'fit' ? 'Check workspace answers' : nameOf(k));
  const jumpOpen = () => { const k = openKeys(true)[0]; if (k) focusAnswer(k, k === 'fit' ? 'button' : undefined); };
  const cells: StripCell[] = [
    {
      key: 'inh', top: inhRows.length, names: inhRows.map((r) => SYSTEM_ROW_NAME[r]).join(', ') || 'None',
      chips: <><ScopeChip kind="inherit">Inherited from workspace</ScopeChip>{isNew && !fitSaved && !fit ? <ScopeChip kind="todo">To check</ScopeChip> : null}{cellChanged(inhRows)}</>,
      onClick: () => focusAnswer(inhRows[0] ?? 'markets'),
    },
    {
      key: 'set', top: setRows.length, tone: setRows.length ? 'hot' : undefined, names: setRows.map((r) => SYSTEM_ROW_NAME[r]).join(', ') || 'None',
      chips: <><ScopeChip kind="set">Set for this system</ScopeChip>{cellChanged(setRows)}</>, onClick: () => focusAnswer(setRows[0] ?? 'markets'),
    },
    openDraft.length
      ? {
        key: 'todo', top: openDraft.length, tone: 'todo', names: openDraft.map(openName).join(', '),
        chips: <ScopeChip kind="todo">To answer</ScopeChip>,
        action: canEdit ? (
          <Button variant="primary" compact onClick={jumpOpen}>
            {openDraft.length === 1 && openDraft[0] === 'fit' ? 'Check workspace answers' : openDraft.length === 1 ? 'Answer the open question' : `Answer ${openDraft.length} open questions`}
          </Button>
        ) : <span className="sc-names">An owner or admin answers these</span>,
      }
      : openSaved.length
        ? { key: 'todo', top: `${total} of ${total}`, tone: 'todo', names: 'System answers', chips: <ScopeChip kind="changed">Answered, not saved</ScopeChip>,
          action: <Button variant="primary" compact onClick={() => void review()}>Review changes</Button> }
        : { key: 'todo', top: `${total} of ${total}`, names: 'System answers', chips: <ScopeChip kind="check">All answered</ScopeChip>, onClick: () => focusAnswer('final_say') },
  ];

  // ---- Rows ----
  const decisionsNone = !row('decision_types').value.some((v) => v !== 'none');
  const settingsHref = `/w/${slug}/app/settings/profile`;
  const draftRows = Object.fromEntries(ROW_KEYS.map((r) => [r, row(r).value])) as Record<RowKey, string[]>;
  const savedRows = Object.fromEntries(ROW_KEYS.map((r) => [r, saved.rows[r].value])) as Record<RowKey, string[]>;
  /** "Markets: Canada" with the saved (before) or draft (after) answers. */
  const depLine = (key: string, after: boolean, jurisdiction?: string) =>
    depText(key, after ? draftRows : savedRows, (k) => onlyWords(k, after ? only : saved.only), jurisdiction);

  /** "Leads to": the rules that apply now and read this answer, each with the other answers it needs. */
  function leadsTo(answer: string) {
    const rules = current.filter((x) => depRows(x.depends_on).includes(answer));
    if (!rules.length) return null;
    const li = (x: Rule) => {
      const others = depRows(x.depends_on).filter((k) => k !== answer).map((k) => depLine(k, false, x.jurisdiction)).filter(Boolean).join(' + ');
      return (
        <li key={x.rule_id}>
          <span className="sc-rule-name">{x.statute}</span>{x.citation ? <span className="sc-cite">{` · ${x.citation}`}</span> : null}
          {others ? <span className="sc-with">{` with ${others}`}</span> : null}
        </li>
      );
    };
    const head = rules.slice(0, 3), rest = rules.slice(3);
    return (
      <div className="sc-leads">
        <span className="sc-leads-lbl">Leads to</span>
        <ul role="list">{head.map(li)}</ul>
        {rest.length ? <details><summary><Caret />{`${rest.length} more`}</summary><ul role="list">{rest.map(li)}</ul></details> : null}
      </div>
    );
  }

  function inheritableRow(r: RowKey) {
    const d = row(r), s = saved.rows[r];
    const dirty = rowDirty(r);
    const wsVal = wsValue(r);
    const sectorMeta = saved.meta?.sector;
    let state = rowState(r, d) as 'set' | 'open' | 'inherit' | 'waiting';
    if (r === 'sector' && d.own && s.own && sameSet(d.value, s.value) && sectorMeta?.waiting) state = 'waiting';
    const name = SYSTEM_ROW_NAME[r];
    const meta = rowMeta(saved.meta, r);
    if (state === 'inherit') {
      return (
        <ScopeAnswerRow key={r} answer={r} name={name} state="inherit" dirty={dirty} canEdit={canEdit}
          workspaceItems={valueItems(r, wsVal)}
          onChange={() => {
            editRow(r, { own: true, value: [...wsVal] });
            say(`${name} is now set for this system.`);
            window.setTimeout(() => focusAnswer(r, 'input, button.sc-pick'), 0);
          }}
          emptyAction={<Link to={settingsHref}>Answer in workspace defaults</Link>}>
          {leadsTo(r)}
        </ScopeAnswerRow>
      );
    }
    if (state === 'open' || state === 'waiting') {
      const pick = (code: string) => {
        if (state === 'waiting' && s.value[0] === code) { confirmAsIs('sector'); return; }
        editRow('sector', { own: true, value: [code] });
        say(`${fmt('sector', [code])} picked.`);
        // The open row becomes a set row: keep focus on the picked sector.
        window.setTimeout(() => focusAnswer('sector', '.sc-pick[aria-pressed="true"]'), 0);
      };
      return (
        <ScopeAnswerRow key={r} answer={r} name={name} state={state} dirty={dirty} waitingOn={fmt('sector', s.value)}>
          {canEdit ? (
            <>
              <p className="sc-hint" id="pick-hint">Pick one</p>
              <SectorPicks value={state === 'waiting' ? s.value[0] ?? null : null} workspaceIndustries={wsIndustries} onPick={pick} describedBy="st-sector pick-hint" />
            </>
          ) : <p className="sc-hint">An owner or admin picks this system’s sector.</p>}
          {leadsTo(r)}
          <span className="sc-meta">{`Workspace names ${wsIndustries.length ? `several industries: ${fmt('sector', wsIndustries)}` : 'no industry'}`}</span>
        </ScopeAnswerRow>
      );
    }
    // Set for this system.
    const editor = !canEdit
      ? <ul className="sc-value" role="list">{valueItems(r, d.value).length ? valueItems(r, d.value).map((x) => <li key={x}>{x}</li>) : <li>Not answered</li>}</ul>
      : r === 'sector'
        ? <SectorPicks value={d.value[0] ?? null} workspaceIndustries={wsIndustries} describedBy={`st-${r} cmp-${r}`}
          onPick={(code) => { editRow('sector', { own: true, value: [code] }); say(`${fmt('sector', [code])} picked.`); }} />
        : <RowEditor row={r} stem={r} value={d.value} saved={s.value} describedBy={`st-${r} cmp-${r}${ROW_HINT[r] ? ` d-${r}` : ''}`}
          onChange={(next, cleared) => {
            editRow(r, { own: true, value: next });
            if (cleared?.length) say(`Cleared ${cleared.map((c) => valueItems(r, [c])[0]).join(', ')}.`);
          }} />;
    const sectorLocked = r === 'sector' && locked;
    return (
      <ScopeAnswerRow key={r} answer={r} name={name} hint={ROW_HINT[r]} state="set" dirty={dirty} canEdit={canEdit} meta={meta}
        pendingAsIs={asIs.has(r)} onUndoAsIs={() => undoAsIs(r)}
        sameAsWorkspace={!sectorLocked && sameSet(d.value, wsVal)}
        workspaceText={sectorLocked ? `Workspace names several industries: ${fmt('sector', wsIndustries)}` : `Workspace: ${fmt(r, wsVal)}`}
        onReset={sectorLocked ? undefined : () => {
          editRow(r, { own: false, value: [...wsVal] });
          say(`${name} follows the workspace again once you confirm.`);
          window.setTimeout(() => focusAnswer(r, 'button.sc-link'), 0);
        }}
        onConfirmAsIs={() => confirmAsIs(r)}>
        {editor}
        {leadsTo(r)}
      </ScopeAnswerRow>
    );
  }

  function onlyRow(k: OnlyKey, body: ReactNode, hint?: string, extra?: ReactNode) {
    return (
      <ScopeAnswerRow key={k} answer={k} name={ONLY_NAME[k]} hint={hint} state="only" answered={answered(k, saved.only) && answered(k, only)}
        dirty={onlyDirty(k)} canEdit={canEdit} meta={saved.meta?.[k as keyof ScopeOverrideMeta] ?? null}
        pendingAsIs={asIs.has(k)} onUndoAsIs={() => undoAsIs(k)} onConfirmAsIs={answered(k, saved.only) ? () => confirmAsIs(k) : undefined}>
        {body}{extra}{leadsTo(k)}
      </ScopeAnswerRow>
    );
  }
  const readValue = (txt: string) => <ul className="sc-value" role="list"><li>{txt}</li></ul>;
  const radios = (k: 'final_say' | 'exposure' | 'gpai_role', choices: { value: string; label: string }[]) => (canEdit ? (
    <fieldset className="sc-opts" data-cols="1" aria-labelledby={`h-${k}`} aria-describedby={`st-${k}${k === 'final_say' ? ' note-final_say' : ''}`}>
      {choices.map((o) => <Radio key={o.value} id={`${k}-${o.value}`} name={k} label={o.label} checked={only[k] === o.value} onChange={() => editOnly(k, o.value)} />)}
    </fieldset>
  ) : readValue(only[k] ? choiceLabel(choices, only[k]!) : 'Not answered'));

  const embedded = only.embedded_third_party_ai;
  const addToken = () => {
    const v = tok.trim();
    if (!v) { setTokErr('Type a model or service name'); say('Type a model or service name'); return; }
    if ((embedded ?? []).includes(v)) { setTokErr(`${v} is already listed`); say(`${v} is already listed`); return; }
    setTokErr(''); setTok(''); editOnly('embedded_third_party_ai', [...(embedded ?? []), v]); say(`Added ${v}.`);
  };

  const fitNames = ROW_KEYS.filter((r) => rowState(r) === 'inherit').map((r) => `${SYSTEM_ROW_NAME[r]}: ${fmt(r, wsValue(r))}`).join(' · ');
  const fitBar = isNew && canEdit ? (
    <div className="sc-fit" id="a-fit" data-state={fitSaved ? 'ok' : fit ? 'pending' : 'ask'}>
      {fitSaved ? <ScopeChip kind="check">Workspace answers checked</ScopeChip> : fit ? (
        <>
          <div className="sc-fit-text"><b>These workspace answers fit this system</b><span>{fitNames}</span></div>
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}><ScopeChip kind="changed">Changed</ScopeChip>
            <button type="button" className="sc-link" onClick={() => { setFit(false); say('Workspace answers check undone.'); }}>Undo</button></span>
        </>
      ) : (
        <>
          <div className="sc-fit-text"><b id="fit-h">{`Do these workspace answers fit ${system.name}?`}</b><span id="fit-names">{fitNames}</span></div>
          <Button compact aria-describedby="fit-h fit-names" onClick={() => { clearDone(); setFit(true); say('Workspace answers marked as fitting this system.'); }}>These fit</Button>
        </>
      )}
    </div>
  ) : null;

  // ---- Sheet ----
  const p = sheet.preview;
  const A = p?.added.length ?? 0, R = p?.removed.length ?? 0;
  const noun = A + R === 1 ? 'obligation' : 'obligations';
  const title = A || R
    ? <><span aria-hidden="true">{`${[A ? `+${A}` : '', R ? `−${R}` : ''].filter(Boolean).join(' / ')} ${noun}`}</span>
      <span className="sr-only">{[A ? `Adds ${plural(A, 'obligation', 'obligations')}` : '', R ? `removes ${plural(R, 'obligation', 'obligations')}` : ''].filter(Boolean).join(', ')}</span></>
    : p ? 'No obligation changes' : 'Couldn’t check what this changes';
  const pageKeys = new Set<string>([...ROW_KEYS, ...ONLY_KEYS]);
  /** Every answer the rule depends on (draft values for an added rule, saved for a removed one); the ones this edit
   *  changed carry "↩". Each one is a way back to its answer. */
  const because = (x: Rule, add: boolean) => {
    const deps = depRows(x.depends_on);
    const keys = deps.length ? deps : dirtyKeys.filter((k) => k !== 'fit');
    return (
      <span className="sc-because">
        {keys.filter((k) => depLine(k, add, x.jurisdiction) !== null).map((k, i) => {
          const changed = (dirtyKeys as string[]).includes(k);
          const text = depLine(k, add, x.jurisdiction);
          return (
            <span key={k}>{i ? <span aria-hidden="true">{' + '}<span className="sr-only"> and </span></span> : null}
              {pageKeys.has(k) ? (
                <button type="button" className="sc-link" data-changed={changed || undefined} onClick={() => closeSheet(k as Key)}>
                  {changed ? <span aria-hidden="true">↩ </span> : null}{text}<span className="sr-only"> (go to this answer)</span>
                </button>
              ) : <span>{text}</span>}
            </span>
          );
        })}
      </span>
    );
  };
  const ruleLi = (x: Rule, add: boolean) => (
    <li key={`${add ? 'a' : 'r'}-${x.rule_id}`}>
      <span className="sc-k" data-k={add ? 'add' : 'rem'}>{add ? '+ Adds' : '− Removes'}</span>
      <span style={{ display: 'grid', gap: 2 }}>
        <span className="sc-rule-name">{x.statute}{x.citation ? <span className="sc-cite">{` · ${x.citation}`}</span> : null}</span>
        {!add ? <span>{REMOVE_NOTE}</span> : null}
        {because(x, add)}
      </span>
    </li>
  );
  const sheetHead = p ? (
    <>
      <p className="sc-count">{`${p.before_count} → ${p.after_count} obligations on ${system.name}`}</p>
      <ul className="sc-changes">{dirtyKeys.map((k) => (
        <li key={k}><button type="button" className="sc-link" onClick={() => closeSheet(k)}><b>{nameOf(k)}</b></button> {changeText(k)}</li>
      ))}</ul>
      {openDraft.length ? (
        <p className="sc-need"><ScopeChip kind="todo">{`${plural(openDraft.length, 'answer', 'answers')} still open`}</ScopeChip>
          <span style={{ color: 'var(--text-muted)' }}>Obligations they drive aren’t counted</span>
          <button type="button" className="sc-link" onClick={() => closeSheet(openDraft[0])}>Answer now</button></p>
      ) : null}
    </>
  ) : null;

  return (
    <div className="sc-root">
      <ScopeSummaryStrip label={`${system.name} scope summary`} cells={cells} />
      {done}

      <section className="sc-section" aria-labelledby="sc-shared-h">
        <div className="sc-section-head">
          <h2 id="sc-shared-h">From workspace defaults</h2>
          <Link to={settingsHref} className="sc-link" style={{ textDecoration: 'underline' }}>Edit workspace defaults</Link>
        </div>
        <div className="sc-card">
          {fitBar}
          {ROW_KEYS.map(inheritableRow)}
        </div>
      </section>

      <section className="sc-section" aria-labelledby="sc-own-h">
        <div className="sc-section-head"><h2 id="sc-own-h">This system only</h2></div>
        <div className="sc-card">
          {onlyRow('final_say', radios('final_say', FINAL_SAY_CHOICES), undefined,
            systemDecides(only.final_say) && decisionsNone ? (
              <p className="sc-note" id="note-final_say"><ScopeChip kind="todo">The system decides, but no decision type is set.</ScopeChip>
                {canEdit ? <button type="button" className="sc-link" onClick={() => {
                  if (!row('decision_types').own) editRow('decision_types', { own: true, value: [...wsValue('decision_types')] });
                  say('Decision types is now set for this system.');
                  window.setTimeout(() => focusAnswer('decision_types', 'input'), 0);
                }}>Set one?</button> : null}</p>
            ) : null)}

          <ScopeAnswerRow answer="your_role" name="Your role" hint="EU AI Act · saved on its own" state="plain">
            <ActorRoleQuestion system={system} canEdit={canEdit} onSaved={onSaved} embedded onDirtyChange={setRoleDirty} />
          </ScopeAnswerRow>

          {onlyRow('gpai_role', radios('gpai_role', GPAI_CHOICES))}

          {onlyRow('exposure', radios('exposure', EXPOSURE_CHOICES), 'Who can reach it')}

          {onlyRow('output_destinations', canEdit ? (
            <fieldset className="sc-opts" aria-labelledby="h-output_destinations" aria-describedby="st-output_destinations">
              {[...OUTPUT_CHOICES, ...only.output_destinations.filter((v) => !OUTPUT_CHOICES.some((o) => o.value === v)).map((v) => ({ value: v, label: choiceLabel(OUTPUT_CHOICES, v) }))].map((o) => (
                <Checkbox key={o.value} id={`outputs-${o.value}`} label={o.label} checked={only.output_destinations.includes(o.value)}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    editOnly('output_destinations', on ? [...only.output_destinations, o.value] : only.output_destinations.filter((x) => x !== o.value));
                  }} />
              ))}
            </fieldset>
          ) : readValue(onlyWords('output_destinations', only) ?? 'Not answered'))}

          {onlyRow('embedded_third_party_ai', canEdit ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {embedded?.length ? (
                <ul className="sc-tokens" aria-labelledby="h-embedded_third_party_ai">{embedded.map((t) => (
                  <li key={t} className="sc-tok">{t}
                    <button type="button" className="sc-link" aria-label={`Remove ${t}`} onClick={() => { editOnly('embedded_third_party_ai', embedded.filter((x) => x !== t)); say(`Removed ${t}.`); window.setTimeout(() => document.getElementById('embedded-add')?.focus(), 0); }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" /></svg>
                    </button></li>
                ))}</ul>
              ) : null}
              <div className="sc-tok-add">
                <Input id="embedded-add" aria-labelledby="h-embedded_third_party_ai" aria-describedby="d-embedded_third_party_ai embedded-err" placeholder="Add a model or service" value={tok}
                  onChange={(e) => { setTok(e.target.value); setTokErr(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToken(); } }} />
                <Button onClick={addToken}>Add</Button>
              </div>
              <p className="sc-err" id="embedded-err" hidden={!tokErr}>{tokErr}</p>
              {!embedded?.length ? (
                <Checkbox id="embedded-none" label="None" checked={embedded !== null && embedded.length === 0}
                  onChange={(e) => editOnly('embedded_third_party_ai', e.currentTarget.checked ? [] : null)} />
              ) : null}
            </div>
          ) : readValue(onlyWords('embedded_third_party_ai', only) ?? 'Not answered'), 'Models or AI services inside it')}

          {onlyRow('deployment_target', canEdit ? (
            <Select id="deployment-target" aria-labelledby="h-deployment_target" aria-describedby="st-deployment_target" value={only.deployment_target ?? ''}
              onChange={(e) => editOnly('deployment_target', e.target.value || null)}>
              <option value="">Not answered</option>
              {TARGET_CHOICES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          ) : readValue(onlyWords('deployment_target', only) ?? 'Not answered'))}

          {onlyRow('security_levels', canEdit ? (
            <fieldset className="sc-cia" aria-labelledby="h-security_levels" aria-describedby="st-security_levels" style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              {CIA_DIMS.map(([d, l]) => (
                <Field key={d} id={`cia-${d}`} label={l}>
                  <Select id={`cia-${d}`} value={only.security_levels[d] ?? ''} onChange={(e) => editOnly('security_levels', { ...only.security_levels, [d]: e.target.value })}>
                    <option value="">Not answered</option>
                    {LEVEL_CHOICES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </Field>
              ))}
            </fieldset>
          ) : readValue(onlyWords('security_levels', only) ?? 'Not answered'))}
        </div>
      </section>

      {canEdit ? (
        <ScopeSaveBar
          names={dirtyKeys.map(nameOf)}
          open={sheet.open}
          loading={sheet.loading}
          busy={busy}
          title={title}
          head={sheetHead}
          ready={!!sheet.preview}
          error={sheet.error}
          onRetry={() => void (sheet.failed === 'save' ? confirm() : review())}
          retryLabel={sheet.failed === 'save' ? 'Try again' : 'Check again'}
          held={roleDirty ? ['Your role'] : []}
          resultText={sheet.preview ? (A || R ? [A ? `Adds ${plural(A, 'obligation', 'obligations')}` : '', R ? `removes ${plural(R, 'obligation', 'obligations')}` : ''].filter(Boolean).join(', ') : 'No obligation changes') : undefined}
          reasonWhere={dirtyKeys.filter((k) => k !== 'fit').map(nameOf).join(', ') || undefined}
          reason={reason}
          onReason={setReason}
          onReview={() => void review()}
          onDiscard={discard}
          onBack={() => closeSheet()}
          onConfirm={() => void confirm()}
          announcement={announcement}
        >
          {p && (A || R) ? <ul className="sc-rules">{p.added.map((x) => ruleLi(x, true))}{p.removed.map((x) => ruleLi(x, false))}</ul> : null}
        </ScopeSaveBar>
      ) : null}
    </div>
  );
}
